import { InputBase, InputBaseProps } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import React from "react";
import { NumericFormat, NumericFormatProps, InputAttributes } from "react-number-format";

const SecondaryInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiInputBase-input": {
    position: "relative",
    borderRadius: "4px",
    fontSize: 14,
    width: "100%",
    padding: "10px 10px",
    transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    "&:focus": {
      boxShadow: `${alpha(theme.palette.secondary.main, 0.7)} 0 0 0 0.2rem`,
      borderColor: theme.palette.secondary.main,
    },
  },
}));

const SecondaryNumberInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiInputBase-input": {
    position: "relative",
    borderRadius: "4px",
    fontSize: 16,
    width: "100%",
    padding: "10px 12px",
    transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    "&:focus": {
      boxShadow: `${alpha(theme.palette.secondary.main, 0.7)} 0 0 0 0.2rem`,
      borderColor: theme.palette.secondary.main,
    },
    "&::placeholder": {
      opacity: 0.7,
    },
  },
}));

interface CustomProps {
  onChange: (event: { event: any; target: { name: string; value: string } }) => void;
  name: string;
}

// NumberFormatCustom makes SecondaryNumberInput render the user input with commas to easily make the input for eligibile
const NumberFormatCustom = React.forwardRef<NumericFormatProps<InputAttributes>, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    // remove ownerState as this causes an error in react due to package
    const { ownerState, ...propsRemovedOwnerState } = other as any;

    return (
      <NumericFormat
        {...propsRemovedOwnerState}
        getInputRef={ref}
        onValueChange={(values, src) => {
          onChange({
            event: src.event,
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        isNumericString
        allowLeadingZeros={false}
        allowNegative={false}
        style={{
          border: "none",
          outline: "none",
          height: "43px",
          background: "transparent",
        }}
      />
    );
  },
);

interface SecondaryInputFieldProps extends InputBaseProps {
  inputType?: "text" | "number";
}

const SecondaryInputField = (props: SecondaryInputFieldProps): JSX.Element => {
  const { placeholder, id, sx, inputType, value, onChange, disabled, defaultValue, endAdornment } =
    props;

  return (
    <>
      {inputType === "text" && (
        <SecondaryInput
          placeholder={placeholder}
          id={id}
          sx={sx}
          value={value}
          onChange={onChange}
          disabled={disabled}
          defaultValue={defaultValue}
          endAdornment={endAdornment}
          autoComplete="off"
        />
      )}

      {inputType === "number" && (
        <SecondaryNumberInput
          placeholder={placeholder}
          id={id}
          sx={sx}
          components={{ Input: NumberFormatCustom as any }}
          value={value}
          onChange={onChange}
          disabled={disabled}
          endAdornment={endAdornment}
          autoComplete="off"
        />
      )}
    </>
  );
};

export default SecondaryInputField;
